import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import RestaurantIcon from '@mui/icons-material/Restaurant';

interface UserProfile {
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  phone: string | null;
  age: number | null;
  gender: string | null;
  height: string | null;
  weight: string | null;
  profilePicture: string | null;
  createdAt: string | null;
}

interface FoodLogUser {
  UID: string;
  logCount: number;
  firstLogDate: string;
  lastLogDate: string;
  totalCaloriesLogged: number;
  activeDays: number;
  profile: UserProfile;
}

interface ApiResponse {
  success: boolean;
  data: {
    users: FoodLogUser[];
    totalUsers: number;
    totalLogEntries: number;
    filters: {
      startDate?: string;
      endDate?: string;
      dateRangeApplied: boolean;
    };
    fetchedAt: string;
  };
}

interface FoodEntry {
  foodName: string;
  mealType: string;
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
  fiber: number;
  sodium: number;
  imageUrl?: string;
  timestamp: string;
}

interface DailyLog {
  date: string;
  totalCalories: number;
  totalCarbs: number;
  totalProtein: number;
  totalFat: number;
  totalFiber: number;
  targetCalories: number;
  calorieDeficit: number;
  calorieExcess: number;
  calorieGoalMet: boolean;
  breakfastCalories: number;
  lunchCalories: number;
  dinnerCalories: number;
  foodEntries: FoodEntry[];
}

interface UserDetailResponse {
  success: boolean;
  data: {
    UID: string;
    logs: DailyLog[];
    totalLogs: number;
    summary: {
      totalCaloriesAllLogs: number;
      averageCalories: number;
      daysWithLogs: number;
      targetCalories: number;
      daysMetGoal: number;
      goalComplianceRate: number;
    };
    filters: {
      startDate?: string;
      endDate?: string;
      dateRangeApplied: boolean;
    };
    fetchedAt: string;
  };
}

const FoodLogPage: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<FoodLogUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalLogEntries, setTotalLogEntries] = useState(0);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<FoodLogUser | null>(null);
  const [userDetailData, setUserDetailData] = useState<UserDetailResponse['data'] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);

  const fetchFoodLogUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }

      const queryString = params.toString();
      const url = `https://api.profile.ayurai.in/getFoodLoggingUsers${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        setUsers(data.data.users);
        setTotalUsers(data.data.totalUsers);
        setTotalLogEntries(data.data.totalLogEntries);
      } else {
        throw new Error('API returned success: false');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch food log data');
      setUsers([]);
      setTotalUsers(0);
      setTotalLogEntries(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (uid: string) => {
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      }

      const queryString = params.toString();
      const url = `https://api.profile.ayurai.in/getUserCalorieLogs/${uid}${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: UserDetailResponse = await response.json();
      
      if (data.success) {
        setUserDetailData(data.data);
      } else {
        throw new Error('API returned success: false');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRowClick = (user: FoodLogUser) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
    fetchUserDetail(user.UID);
  };

  const handleCloseDialog = () => {
    setDetailDialogOpen(false);
    setSelectedUser(null);
    setUserDetailData(null);
    setDialogTab(0);
  };

  // CSV Export Functions
  const convertToCSV = (data: any[], headers: string[]) => {
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.join(','));
    
    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        // Escape quotes and wrap in quotes if contains comma
        const escaped = ('' + (value ?? '')).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const calculateReward = (activeDays: number) => {
    // ₹50 per week (7 days) of logging
    const weeksCompleted = Math.floor(activeDays / 7);
    return weeksCompleted * 50;
  };

  const exportUsersToCSV = () => {
    const data = filteredUsers.map(user => {
      const reward = calculateReward(user.activeDays);
      return {
        UID: user.UID,
        Name: user.profile ? `${user.profile.firstname || ''} ${user.profile.lastname || ''}`.trim() : 'N/A',
        Email: user.profile?.email || 'N/A',
        Phone: user.profile?.phone || 'N/A',
        LogCount: user.logCount,
        ActiveDays: user.activeDays,
        WeeksCompleted: Math.floor(user.activeDays / 7),
        RewardEarned: reward,
        TotalCalories: user.totalCaloriesLogged,
        FirstLogDate: user.firstLogDate,
        LastLogDate: user.lastLogDate,
      };
    });
    
    const headers = ['UID', 'Name', 'Email', 'Phone', 'LogCount', 'ActiveDays', 'WeeksCompleted', 'RewardEarned', 'TotalCalories', 'FirstLogDate', 'LastLogDate'];
    const csv = convertToCSV(data, headers);
    const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    downloadCSV(csv, `food-log-users_${timestamp}.csv`);
  };

  const exportUserDetailToCSV = () => {
    if (!userDetailData || !selectedUser) return;
    
    const activeDays = selectedUser.activeDays;
    const weeksCompleted = Math.floor(activeDays / 7);
    const rewardEarned = calculateReward(activeDays);
    
    // Flatten the data: each row is one food entry
    const data: any[] = [];
    
    userDetailData.logs.forEach(log => {
      log.foodEntries.forEach(entry => {
        data.push({
          Date: log.date,
          MealType: entry.mealType,
          FoodName: entry.foodName,
          Calories: entry.calories,
          Carbs: entry.carbs,
          Protein: entry.protein,
          Fat: entry.fat,
          Fiber: entry.fiber,
          Sodium: entry.sodium,
          Timestamp: entry.timestamp,
          DayTotalCalories: log.totalCalories,
          DayTargetCalories: log.targetCalories,
          GoalMet: log.calorieGoalMet ? 'Yes' : 'No',
          ActiveDays: activeDays,
          WeeksCompleted: weeksCompleted,
          RewardEarned: rewardEarned,
        });
      });
    });
    
    const headers = ['Date', 'MealType', 'FoodName', 'Calories', 'Carbs', 'Protein', 'Fat', 'Fiber', 'Sodium', 'Timestamp', 'DayTotalCalories', 'DayTargetCalories', 'GoalMet', 'ActiveDays', 'WeeksCompleted', 'RewardEarned'];
    const csv = convertToCSV(data, headers);
    const userName = selectedUser.profile 
      ? `${selectedUser.profile.firstname || ''}_${selectedUser.profile.lastname || ''}`.trim().replace(/\s+/g, '_')
      : selectedUser.UID;
    const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    downloadCSV(csv, `food-log_${userName}_${timestamp}.csv`);
  };

  useEffect(() => {
    fetchFoodLogUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(
    () =>
      users
        .filter((user) => {
          if (!searchQuery.trim()) return true;
          const query = searchQuery.toLowerCase();
          const fullName = user.profile
            ? `${user.profile.firstname || ''} ${user.profile.lastname || ''}`.toLowerCase()
            : '';
          const email = user.profile?.email?.toLowerCase() || '';
          return (
            user.UID.toLowerCase().includes(query) ||
            fullName.includes(query) ||
            email.includes(query)
          );
        })
        .sort((a, b) => {
          // Primary sort: by active days (descending)
          if (b.activeDays !== a.activeDays) {
            return b.activeDays - a.activeDays;
          }
          // Secondary sort: by log count (descending) if active days are equal
          return b.logCount - a.logCount;
        }),
    [users, searchQuery]
  );

  const getRankColor = (index: number) => {
    if (index === 0) return '#FFD700'; // Gold
    if (index === 1) return '#C0C0C0'; // Silver
    if (index === 2) return '#CD7F32'; // Bronze
    return null;
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'rank',
        headerName: 'Rank',
        width: 80,
        renderCell: (params) => {
          const index = filteredUsers.findIndex((u) => u.UID === params.row.UID);
          const rankColor = getRankColor(index);

          if (rankColor) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmojiEventsIcon sx={{ color: rankColor, fontSize: 24 }} />
                <Typography sx={{ fontWeight: 600 }}>#{index + 1}</Typography>
              </Box>
            );
          }
          return <Typography>#{index + 1}</Typography>;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
          const user = params.row as FoodLogUser;
          if (!user.profile) return 'N/A';
          const name = `${user.profile.firstname || ''} ${user.profile.lastname || ''}`.trim();
          return name || 'N/A';
        },
      },
      {
        field: 'UID',
        headerName: 'User ID',
        width: 280,
      },
      {
        field: 'logCount',
        headerName: 'Log Count',
        width: 100,
        type: 'number',
      },
      {
        field: 'activeDays',
        headerName: 'Active Days',
        width: 100,
        type: 'number',
      },
      {
        field: 'totalCaloriesLogged',
        headerName: 'Total Calories',
        width: 120,
        type: 'number',
        renderCell: (params) => {
          return params.value?.toLocaleString() || '0';
        },
      },
      {
        field: 'firstLogDate',
        headerName: 'First Log',
        width: 110,
      },
      {
        field: 'lastLogDate',
        headerName: 'Last Log',
        width: 110,
      },
      {
        field: 'reward',
        headerName: 'Reward (₹)',
        width: 50,
        type: 'number',
        renderCell: (params) => {
          const user = params.row as FoodLogUser;
          const reward = Math.floor(user.activeDays / 7) * 50;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 600, color: reward > 0 ? '#2e7d32' : 'inherit' }}>
                ₹{reward}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [filteredUsers]
  );

  return (
    <Box
      sx={{
        p: 3,
        pt: 1.5,
        bgcolor: '#fafafa',
        minHeight: '100vh',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <RestaurantIcon sx={{ fontSize: 40, color: '#DA6E0B' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#3B362A' }}>
              Food Log
            </Typography>
            <Typography variant="body2" sx={{ color: '#75623f', mt: 0.5 }}>
              Track and manage user food logs and nutrition data
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search by User ID, Name, Email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter search term..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{ height: '40px' }}
                onClick={fetchFoodLogUsers}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Search'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                sx={{ height: '40px' }}
                onClick={exportUsersToCSV}
                disabled={loading || filteredUsers.length === 0}
                startIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Stats */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          gap: 3,
          flexWrap: 'wrap',
          alignItems: 'stretch',
        }}
      >
        <Card sx={{ 
          flex: 1, 
          borderRadius: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
              Total Users
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#3B362A' }}>
              {totalUsers}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ 
          flex: 1, 
          borderRadius: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
              Total Log Entries
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#3B362A' }}>
              {totalLogEntries}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ 
          flex: 1, 
          borderRadius: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
              Filtered Results
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#3B362A' }}>
              {filteredUsers.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Data Grid */}
      <Card sx={{ 
        borderRadius: 2, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        bgcolor: 'white',
        border: '1px solid #e0e0e0',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#3B362A' }}>
            User Food Logs
          </Typography>
          <Box
            sx={{
              height: 600,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
            }}
          >
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              getRowId={(row) => row.UID}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              loading={loading}
              onRowClick={(params) => handleRowClick(params.row)}
              sx={{
                cursor: 'pointer',
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#3B362A',
                  color: 'white',
                  fontSize: '14px',
                },
                '& .MuiDataGrid-columnHeader': {
                  padding: '12px',
                  '&:focus': {
                    outline: 'none',
                  },
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  color: 'white',
                },
                '& .MuiDataGrid-iconSeparator': {
                  color: 'rgba(255,255,255,0.5)',
                },
                '& .MuiDataGrid-sortIcon': {
                  color: 'white',
                },
                '& .MuiDataGrid-menuIcon': {
                  color: 'white',
                },
                '& .MuiDataGrid-cell': {
                  padding: '12px',
                  borderBottom: '1px solid #f0f0f0',
                  '&:focus': {
                    outline: 'none',
                  },
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: '#F4ECD6',
                  },
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid #f0f0f0',
                  backgroundColor: '#fafafa',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#3B362A',
            color: 'white',
            pb: 3,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {selectedUser?.profile 
                  ? `${selectedUser.profile.firstname || ''} ${selectedUser.profile.lastname || ''}`.trim() || selectedUser.UID
                  : selectedUser?.UID}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {selectedUser?.UID}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
                onClick={exportUserDetailToCSV}
                disabled={!userDetailData || detailLoading}
                startIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
              <IconButton 
                onClick={handleCloseDialog}
                sx={{ 
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#fafafa', p: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : userDetailData ? (
            <Box>
              {/* Profile Information */}
              {selectedUser && selectedUser.profile && (
                <Card 
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    bgcolor: 'white',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#3B362A', mb: 2 }}>
                      Profile Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                        <Typography variant="body1">
                          {selectedUser.profile.email || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">Phone</Typography>
                        <Typography variant="body1">
                          {selectedUser.profile.phone || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">Gender</Typography>
                        <Typography variant="body1">
                          {selectedUser.profile.gender || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">Height</Typography>
                        <Typography variant="body1">
                          {selectedUser.profile.height || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">Weight</Typography>
                        <Typography variant="body1">
                          {selectedUser.profile.weight || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
                        Total Logs
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B362A' }}>
                        {userDetailData.totalLogs}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
                        Average Calories
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B362A' }}>
                        {userDetailData.summary.averageCalories.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
                        Days Met Goal
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B362A' }}>
                        {userDetailData.summary.daysMetGoal} / {userDetailData.summary.daysWithLogs}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      bgcolor: 'white',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="body2" sx={{ color: '#75623f', mb: 1, fontWeight: 600 }}>
                        Goal Compliance
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3B362A' }}>
                        {(userDetailData.summary.goalComplianceRate).toFixed(0)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Reward Card */}
              <Card 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: '1px solid #e0e0e0',
                  background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1, fontWeight: 600 }}>
                        💰 Reward Earned
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'white' }}>
                        ₹{selectedUser ? calculateReward(selectedUser.activeDays) : 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1, display: 'block' }}>
                        {selectedUser ? Math.floor(selectedUser.activeDays / 7) : 0} weeks completed × ₹50 per week
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      borderRadius: 2, 
                      p: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                        {selectedUser ? Math.floor(selectedUser.activeDays / 7) : 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        Weeks
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs 
                  value={dialogTab} 
                  onChange={(e, newValue) => setDialogTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      color: '#75623f',
                      '&.Mui-selected': {
                        color: '#DA6E0B',
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#DA6E0B',
                    }
                  }}
                >
                  <Tab label="Daily Logs" />
                  <Tab label="Analytics & Trends" />
                </Tabs>
              </Box>

              {/* Tab Content */}
              {dialogTab === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#3B362A', mb: 3 }}>
                    Daily Food Logs
                  </Typography>
                  {userDetailData.logs.map((log, index) => (
                <Accordion 
                  key={index} 
                  sx={{ 
                    mb: 2,
                    borderRadius: '12px !important',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: '#DA6E0B' }} />}
                    sx={{
                      borderRadius: '12px',
                      '&.Mui-expanded': {
                        borderRadius: '12px 12px 0 0',
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#3B362A' }}>
                        📅 {log.date}
                      </Typography>
                      <Chip 
                        label={`${log.totalCalories} cal`} 
                        size="small" 
                        sx={{
                          bgcolor: log.calorieGoalMet ? '#8CEB8C' : '#DA6E0B',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                      <Chip 
                        label={`🍽️ ${log.foodEntries.length} meals`} 
                        size="small" 
                        sx={{
                          bgcolor: '#f0f0f0',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Nutrition Summary */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Daily Nutrition Summary
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">Calories</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {log.totalCalories} / {log.targetCalories}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">Carbs</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {log.totalCarbs}g
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">Protein</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {log.totalProtein}g
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="body2" color="text.secondary">Fat</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {log.totalFat}g
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Meal Breakdown */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
                      🍽️ Meals
                    </Typography>
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid rgba(0,0,0,0.05)',
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#3B362A' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 700 }}>Meal Type</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 700 }}>Food</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Calories</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Carbs</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Protein</TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Fat</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {log.foodEntries.map((entry, idx) => (
                            <TableRow 
                              key={idx}
                              sx={{
                                '&:nth-of-type(odd)': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                },
                                '&:hover': {
                                  backgroundColor: '#F4ECD6',
                                }
                              }}
                            >
                              <TableCell>
                                <Chip 
                                  label={entry.mealType} 
                                  size="small" 
                                  sx={{ 
                                    textTransform: 'capitalize',
                                    fontWeight: 600,
                                    bgcolor: '#DA6E0B',
                                    color: 'white',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {entry.imageUrl && (
                                    <Box
                                      component="img"
                                      src={entry.imageUrl}
                                      alt={entry.foodName}
                                      sx={{ 
                                        width: 50, 
                                        height: 50, 
                                        objectFit: 'cover', 
                                        borderRadius: 2,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                      }}
                                    />
                                  )}
                                  <Typography sx={{ fontWeight: 500 }}>
                                    {entry.foodName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#DA6E0B' }}>
                                {entry.calories}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500 }}>
                                {entry.carbs}g
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500 }}>
                                {entry.protein}g
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500 }}>
                                {entry.fat}g
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
                </Box>
              )}

              {/* Analytics Tab */}
              {dialogTab === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#3B362A', mb: 3 }}>
                    Analytics & Trends
                  </Typography>

                  {/* Calorie Trend Line Chart */}
                  <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3, color: '#3B362A' }}>
                        Daily Calorie Trend
                      </Typography>
                      <Box sx={{ position: 'relative', height: 300, px: 2 }}>
                        {/* Grid lines */}
                        {[0, 25, 50, 75, 100].map((percent) => (
                          <Box
                            key={percent}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: `${percent}%`,
                              height: '1px',
                              bgcolor: '#f0f0f0',
                              zIndex: 0,
                            }}
                          />
                        ))}
                        
                        {/* Target line */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: '50%',
                            height: '2px',
                            bgcolor: '#DA6E0B',
                            opacity: 0.3,
                            zIndex: 1,
                          }}
                        />
                        
                        {/* Line chart */}
                        <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, zIndex: 2 }}>
                          <polyline
                            fill="none"
                            stroke="#DA6E0B"
                            strokeWidth="3"
                            points={userDetailData.logs.map((log, idx) => {
                              const x = (idx / (userDetailData.logs.length - 1)) * 100;
                              const y = 100 - Math.min((log.totalCalories / (userDetailData.summary.targetCalories * 1.5)) * 100, 100);
                              return `${x}%,${y}%`;
                            }).join(' ')}
                          />
                          {userDetailData.logs.map((log, idx) => {
                            const x = (idx / (userDetailData.logs.length - 1)) * 100;
                            const y = 100 - Math.min((log.totalCalories / (userDetailData.summary.targetCalories * 1.5)) * 100, 100);
                            return (
                              <circle
                                key={idx}
                                cx={`${x}%`}
                                cy={`${y}%`}
                                r="6"
                                fill={log.calorieGoalMet ? '#8CEB8C' : '#DA6E0B'}
                                stroke="white"
                                strokeWidth="2"
                                style={{ cursor: 'pointer' }}
                              >
                                <title>{`${log.date}: ${log.totalCalories} cal`}</title>
                              </circle>
                            );
                          })}
                        </svg>

                        {/* X-axis labels */}
                        <Box sx={{ position: 'absolute', bottom: -30, left: 0, right: 0, display: 'flex', justifyContent: 'space-between' }}>
                          {userDetailData.logs.map((log, idx) => (
                            <Typography key={idx} variant="caption" sx={{ fontSize: '10px', color: '#75623f' }}>
                              {log.date.split('-').slice(1).join('/')}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#8CEB8C', borderRadius: '50%', border: '2px solid white' }} />
                          <Typography variant="caption">Goal Met</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, bgcolor: '#DA6E0B', borderRadius: '50%', border: '2px solid white' }} />
                          <Typography variant="caption">Below Goal</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#DA6E0B', opacity: 0.3 }} />
                          <Typography variant="caption">Target: {userDetailData.summary.targetCalories} cal</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Calorie Heatmap */}
                  <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3, color: '#3B362A' }}>
                        Calorie Intake Heatmap
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                        {(() => {
                          const maxCalories = Math.max(...userDetailData.logs.map(l => l.totalCalories));
                          const minCalories = Math.min(...userDetailData.logs.map(l => l.totalCalories));
                          
                          return userDetailData.logs.map((log, idx) => {
                            // Calculate intensity based on calories
                            const intensity = (log.totalCalories - minCalories) / (maxCalories - minCalories);
                            
                            // Color scale from light green to dark orange
                            let color;
                            if (intensity < 0.33) {
                              color = '#8CEB8C'; // Light - Low calories
                            } else if (intensity < 0.66) {
                              color = '#F4ECD6'; // Medium
                            } else {
                              color = '#DA6E0B'; // High calories
                            }
                            
                            return (
                              <Box
                                key={idx}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  bgcolor: color,
                                  borderRadius: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid white',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                  }
                                }}
                                title={`${log.date}: ${log.totalCalories} cal`}
                              >
                                <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 600, color: '#3B362A' }}>
                                  {log.date.split('-')[2]}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 700, color: '#3B362A' }}>
                                  {log.totalCalories}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '8px', color: '#75623f' }}>
                                  cal
                                </Typography>
                              </Box>
                            );
                          });
                        })()}
                      </Box>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Low</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Box sx={{ width: 30, height: 20, bgcolor: '#8CEB8C', borderRadius: 0.5 }} />
                          <Box sx={{ width: 30, height: 20, bgcolor: '#F4ECD6', borderRadius: 0.5 }} />
                          <Box sx={{ width: 30, height: 20, bgcolor: '#DA6E0B', borderRadius: 0.5 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>High</Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Macros Breakdown */}
                  <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 3, color: '#3B362A' }}>
                        Average Macronutrient Distribution
                      </Typography>
                      <Grid container spacing={3}>
                        {(() => {
                          const totalCarbs = userDetailData.logs.reduce((sum, log) => sum + log.totalCarbs, 0);
                          const totalProtein = userDetailData.logs.reduce((sum, log) => sum + log.totalProtein, 0);
                          const totalFat = userDetailData.logs.reduce((sum, log) => sum + log.totalFat, 0);
                          const avgCarbs = Math.round(totalCarbs / userDetailData.logs.length);
                          const avgProtein = Math.round(totalProtein / userDetailData.logs.length);
                          const avgFat = Math.round(totalFat / userDetailData.logs.length);
                          const total = avgCarbs + avgProtein + avgFat;
                          
                          return (
                            <>
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#DA6E0B', mb: 1 }}>
                                    {avgCarbs}g
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#75623f', fontWeight: 600, mb: 1 }}>
                                    Carbohydrates
                                  </Typography>
                                  <Box sx={{ width: '100%', height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ width: `${(avgCarbs / total) * 100}%`, height: '100%', bgcolor: '#DA6E0B' }} />
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#75623f' }}>
                                    {Math.round((avgCarbs / total) * 100)}%
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#8CEB8C', mb: 1 }}>
                                    {avgProtein}g
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#75623f', fontWeight: 600, mb: 1 }}>
                                    Protein
                                  </Typography>
                                  <Box sx={{ width: '100%', height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ width: `${(avgProtein / total) * 100}%`, height: '100%', bgcolor: '#8CEB8C' }} />
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#75623f' }}>
                                    {Math.round((avgProtein / total) * 100)}%
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#75623f', mb: 1 }}>
                                    {avgFat}g
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#75623f', fontWeight: 600, mb: 1 }}>
                                    Fat
                                  </Typography>
                                  <Box sx={{ width: '100%', height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ width: `${(avgFat / total) * 100}%`, height: '100%', bgcolor: '#75623f' }} />
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#75623f' }}>
                                    {Math.round((avgFat / total) * 100)}%
                                  </Typography>
                                </Box>
                              </Grid>
                            </>
                          );
                        })()}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Meal Frequency */}
                  <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#3B362A' }}>
                        Logging Activity
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Average Meals Per Day</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 700, color: '#DA6E0B', my: 1 }}>
                              {(userDetailData.totalLogs / userDetailData.summary.daysWithLogs).toFixed(1)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="body2" color="text.secondary">Most Active Day</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#3B362A', my: 1 }}>
                              {(() => {
                                const maxLog = userDetailData.logs.reduce((max, log) => 
                                  log.foodEntries.length > max.foodEntries.length ? log : max
                                );
                                return `${maxLog.date} (${maxLog.foodEntries.length} meals)`;
                              })()}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">No data available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FoodLogPage;
